import React, { useEffect, useState, useCallback } from "react";
import { Edit, Plus, RotateCw, Save, Trash2, X } from "lucide-react";
import OperatorCardActions from "../components/OperatorCardActions";
import ToolbarSearch from "../components/ToolbarSearch";
import { apiFetch } from "../utils/apiFetch";
import { fetchLocations } from "../services/locations";
import { markOperatorInfoViewed } from "../services/operator_info";
import { API_BASE_OPERATOR } from "../config/vars";

const InfoOperators = ({ onOperatorInfoViewed }) => {
    const [infoList, setInfoList] = useState([]);
    const [filteredInfo, setFilteredInfo] = useState([]);
    const [locations, setLocations] = useState([]);
    const [message, setMessage] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [sortBy, setSortBy] = useState("name");
    const [sortDir, setSortDir] = useState("asc");
    const [editingId, setEditingId] = useState(null);
    const [expandedInfoId, setExpandedInfoId] = useState(null);
    const [editedData, setEditedData] = useState({ name: "", description: "", location_id: null });
    const [originalData, setOriginalData] = useState({});
    const [newInfo, setNewInfo] = useState({ name: "", description: "", location_id: null });
    const [showAddModal, setShowAddModal] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const buildLocationLabel = (loc) => {
        if (!loc) return "";
        return [loc.street, loc.house_number, loc.location_name].filter(Boolean).join(" ");
    };

    const loadLocations = useCallback(async () => {
        try {
            const data = await fetchLocations();
            setLocations(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Помилка завантаження локацій:", error);
        }
    }, []);

    const loadInfo = useCallback(async () => {
        try {
            const res = await apiFetch(`${API_BASE_OPERATOR}/operator-info/operator`);
            if (!res.ok) throw new Error("Не вдалося завантажити дані");
            const data = await res.json();
            setInfoList(data);
        } catch (error) {
            setMessage(`❌ Помилка: ${error.message}`);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                await markOperatorInfoViewed();
                if (!cancelled && typeof onOperatorInfoViewed === "function") onOperatorInfoViewed();
            } catch (_) {
                // ignore mark failures
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadInfo();
        loadLocations();
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [loadInfo, loadLocations]);

    useEffect(() => {
        if (!Array.isArray(infoList) || infoList.length === 0) return;

        let raw = null;
        try {
            raw = localStorage.getItem("pendingOperatorInfoOpenId_operator");
        } catch (_) {
            raw = null;
        }
        if (!raw) return;

        const id = parseInt(raw);
        try {
            localStorage.removeItem("pendingOperatorInfoOpenId_operator");
        } catch (_) {
            // ignore
        }
        if (!Number.isFinite(id) || id <= 0) return;

        const item = infoList.find((x) => Number(x?.id) === id);
        if (item) {
            setExpandedInfoId(item.id);
        }
    }, [infoList]);

    useEffect(() => {
        const q = searchQuery.toLowerCase();
        const filtered = infoList.filter((item) =>
            (item.name || "").toString().toLowerCase().includes(q) ||
            (item.description || "").toString().toLowerCase().includes(q)
        );
        setFilteredInfo(filtered);
    }, [infoList, searchQuery]);

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
    };

    const getSortedData = () => {
        if (!filteredInfo) return [];
        const sorted = [...filteredInfo];
        sorted.sort((a, b) => {
            let valA = a[sortBy] || "";
            let valB = b[sortBy] || "";
            valA = String(valA).toLowerCase();
            valB = String(valB).toLowerCase();
            if (sortDir === "asc") {
                return valA > valB ? 1 : valA < valB ? -1 : 0;
            }
            return valA < valB ? 1 : valA > valB ? -1 : 0;
        });
        return sorted;
    };

    const getPreviewText = (value) => {
        const raw = String(value || "").replace(/\s+/g, " ").trim();
        if (!raw) return "Без опису";
        return raw.length > 120 ? `${raw.slice(0, 120)}...` : raw;
    };

    const toggleExpanded = (id, isEditing) => {
        if (isEditing) return;
        setExpandedInfoId((prev) => (prev === id ? null : id));
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        setExpandedInfoId(item.id);
        setEditedData({
            name: item.name,
            description: item.description || "",
            location_id: item.location_id || null,
        });
        setOriginalData({
            name: item.name,
            description: item.description || "",
            location_id: item.location_id || null,
        });
    };

    const handleCancel = () => {
        setEditedData(originalData);
        setEditingId(null);
    };

    const handleSave = async (id) => {
        try {
            const res = await apiFetch(`${API_BASE_OPERATOR}/operator-info/operator/${id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(editedData),
            });
            if (!res.ok) throw new Error("Не вдалося оновити запис");
            await loadInfo();
            setEditingId(null);
            setMessage("✅ Запис успішно оновлено");
            setTimeout(() => setMessage(""), 3000);
        } catch (error) {
            setMessage(`❌ Помилка: ${error.message}`);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Ви впевнені, що хочете видалити цей запис?")) return;

        try {
            const res = await apiFetch(`${API_BASE_OPERATOR}/operator-info/operator/${id}`, {
                method: "DELETE",
            });
            if (!res.ok) throw new Error("Не вдалося видалити запис");
            if (expandedInfoId === id) setExpandedInfoId(null);
            if (editingId === id) setEditingId(null);
            await loadInfo();
            setMessage("✅ Запис успішно видалено");
            setTimeout(() => setMessage(""), 3000);
        } catch (error) {
            setMessage(`❌ Помилка: ${error.message}`);
        }
    };

    const handleAdd = async () => {
        if (!newInfo.name.trim()) {
            alert("Назва не може бути порожньою!");
            return;
        }
        try {
            const res = await apiFetch(`${API_BASE_OPERATOR}/operator-info/operator`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(newInfo),
            });
            if (!res.ok) throw new Error("Не вдалося створити запис");
            setNewInfo({ name: "", description: "", location_id: null });
            setShowAddModal(false);
            await loadInfo();
            setMessage("✅ Запис успішно створено");
            setTimeout(() => setMessage(""), 3000);
        } catch (error) {
            setMessage(`❌ Помилка: ${error.message}`);
        }
    };

    const renderMobileView = () => (
        <div className="operator-accordion-list">
            {getSortedData().map((item) => {
                const isEditing = editingId === item.id;
                const isExpanded = expandedInfoId === item.id || isEditing;
                const meta = [
                    item.location_name || "Все ОСББ",
                    new Date(item.dtlm).toLocaleString("uk-UA"),
                ].filter(Boolean).join(" • ");

                return (
                    <div key={item.id} className="operator-accordion-item config-card">
                        <button
                            type="button"
                            className="operator-accordion-head"
                            onClick={() => toggleExpanded(item.id, isEditing)}
                            aria-expanded={isExpanded}
                        >
                            <div className="operator-accordion-head-main">
                                <div className="operator-accordion-title">{item.name}</div>
                                <div className="operator-accordion-meta">{meta}</div>
                                {!isExpanded ? (
                                    <div className="operator-accordion-preview">{getPreviewText(item.description)}</div>
                                ) : null}
                            </div>
                            <div className="operator-accordion-caret">{isExpanded ? "▾" : "▸"}</div>
                        </button>

                        <OperatorCardActions
                            editing={isEditing}
                            onEdit={() => handleEdit(item)}
                            onSave={() => handleSave(item.id)}
                            onCancel={handleCancel}
                            onDelete={() => handleDelete(item.id)}
                        />

                        {isExpanded ? (
                            <div className="operator-accordion-body">
                                <div>
                                    <strong>Назва:</strong>{" "}
                                    {isEditing ? (
                                        <input
                                            value={editedData.name}
                                            onChange={(e) => setEditedData({ ...editedData, name: e.target.value })}
                                        />
                                    ) : (
                                        item.name
                                    )}
                                </div>
                                <div>
                                    <strong>Опис:</strong>{" "}
                                    {isEditing ? (
                                        <textarea
                                            value={editedData.description}
                                            onChange={(e) => setEditedData({ ...editedData, description: e.target.value })}
                                            rows={6}
                                        />
                                    ) : (
                                        <span style={{ whiteSpace: "pre-wrap" }}>{item.description || "Без опису"}</span>
                                    )}
                                </div>
                                <div>
                                    <strong>Локація:</strong>{" "}
                                    {isEditing ? (
                                        <select
                                            value={editedData.location_id || ""}
                                            onChange={(e) => setEditedData({
                                                ...editedData,
                                                location_id: e.target.value ? parseInt(e.target.value, 10) : null,
                                            })}
                                        >
                                            <option value="">Все ОСББ</option>
                                            {locations.map((loc) => (
                                                <option key={loc.id} value={loc.id}>
                                                    {buildLocationLabel(loc) || loc.location_name}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        item.location_name || "Все ОСББ"
                                    )}
                                </div>
                                <div>
                                    <strong>Оновлено:</strong> {new Date(item.dtlm).toLocaleString("uk-UA")}
                                </div>
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );

    const renderDesktopView = () => (
        <div className="table-responsive">
            <table>
                <thead>
                    <tr>
                        <th>ID</th>
                        <th style={{ cursor: "pointer" }} onClick={() => toggleSort("name")}>
                            Назва {sortBy === "name" && (sortDir === "asc" ? "▲" : "▼")}
                        </th>
                        <th style={{ cursor: "pointer" }} onClick={() => toggleSort("description")}>
                            Опис {sortBy === "description" && (sortDir === "asc" ? "▲" : "▼")}
                        </th>
                        <th style={{ cursor: "pointer" }} onClick={() => toggleSort("location_id")}>
                            Локація {sortBy === "location_id" && (sortDir === "asc" ? "▲" : "▼")}
                        </th>
                        <th>Дата оновлення</th>
                        <th>Дії</th>
                    </tr>
                </thead>
                <tbody>
                    {getSortedData().map((item) => {
                        const isEditing = editingId === item.id;
                        return (
                            <tr key={item.id}>
                                <td>{item.id}</td>
                                <td>
                                    {isEditing ? (
                                        <input
                                            value={editedData.name}
                                            onChange={(e) => setEditedData({ ...editedData, name: e.target.value })}
                                        />
                                    ) : (
                                        item.name
                                    )}
                                </td>
                                <td>
                                    {isEditing ? (
                                        <textarea
                                            value={editedData.description}
                                            onChange={(e) => setEditedData({ ...editedData, description: e.target.value })}
                                            rows={3}
                                        />
                                    ) : (
                                        getPreviewText(item.description)
                                    )}
                                </td>
                                <td>
                                    {isEditing ? (
                                        <select
                                            value={editedData.location_id || ""}
                                            onChange={(e) => setEditedData({
                                                ...editedData,
                                                location_id: e.target.value ? parseInt(e.target.value, 10) : null,
                                            })}
                                        >
                                            <option value="">Все ОСББ</option>
                                            {locations.map((loc) => (
                                                <option key={loc.id} value={loc.id}>
                                                    {buildLocationLabel(loc) || loc.location_name}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        item.location_name || "Все ОСББ"
                                    )}
                                </td>
                                <td style={{ whiteSpace: "nowrap", fontSize: "12px" }}>
                                    {new Date(item.dtlm).toLocaleString("uk-UA")}
                                </td>
                                <td className="actions">
                                    {isEditing ? (
                                        <>
                                            <button className="icon-button" onClick={() => handleSave(item.id)} title="Зберегти">
                                                <Save size={16} />
                                            </button>
                                            <button className="icon-button" onClick={handleCancel} title="Скасувати">
                                                <X size={16} />
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button className="icon-button" onClick={() => handleEdit(item)} title="Редагувати">
                                                <Edit size={16} />
                                            </button>
                                            <button className="icon-button cancel-btn" onClick={() => handleDelete(item.id)} title="Видалити">
                                                <Trash2 size={16} />
                                            </button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

    return (
        <div>
            <div className="config-header">
                <div className="header-buttons">
                    <button className="add-btn toolbar-icon-btn" onClick={() => setShowAddModal(true)} title="Додати" aria-label="Додати">
                        <Plus size={16} />
                    </button>
                    <button className="add-btn refresh toolbar-icon-btn" onClick={loadInfo} title="Оновити" aria-label="Оновити">
                        <RotateCw size={16} />
                    </button>
                    <ToolbarSearch
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Пошук за назвою або описом..."
                    />
                </div>
            </div>

            {message && <div className="message-banner">{message}</div>}

            {showAddModal && (
                <div className="modal-backdrop" onClick={() => setShowAddModal(false)}>
                    <div className="modal-content operator-mobile-modal operator-form-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Додати інформацію</h3>
                        <div className="operator-form-grid">
                            <input
                                className="operator-form-span-2"
                                type="text"
                                placeholder="Назва*"
                                value={newInfo.name}
                                onChange={(e) => setNewInfo({ ...newInfo, name: e.target.value })}
                            />
                            <textarea
                                className="operator-form-span-2"
                                placeholder="Опис"
                                value={newInfo.description}
                                onChange={(e) => setNewInfo({ ...newInfo, description: e.target.value })}
                            />
                            <select
                                className="operator-form-span-2"
                                value={newInfo.location_id || ""}
                                onChange={(e) => setNewInfo({
                                    ...newInfo,
                                    location_id: e.target.value ? parseInt(e.target.value) : null,
                                })}
                            >
                                <option value="">Все ОСББ</option>
                                {locations.map((loc) => (
                                    <option key={loc.id} value={loc.id}>
                                        {buildLocationLabel(loc) || loc.location_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="modal-actions">
                            <button className="add-btn" onClick={handleAdd}>Додати</button>
                            <button onClick={() => setShowAddModal(false)} className="add-btn cancel-btn">
                                Скасувати
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {infoList.length === 0 ? (
                <p style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                    Немає записів. Натисніть "Додати" для створення.
                </p>
            ) : isMobile ? (
                renderMobileView()
            ) : (
                renderDesktopView()
            )}
        </div>
    );
};

export default InfoOperators;
